/**
 * Add Custom Section Modal
 * Create new custom sections (AI or manual)
 */

import React, { useState } from 'react';
import { Modal, Button, Input, Select } from '../UI';
import { FileText, Brain, Type } from 'lucide-react';
import { type ReportSection } from '../../api/reports';

interface AddCustomSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  onSectionCreated: (section: ReportSection) => void;
  defaultPosition?: number;
}

export function AddCustomSectionModal({
  isOpen,
  onClose,
  reportId,
  onSectionCreated,
  defaultPosition,
}: AddCustomSectionModalProps) {
  const [sectionName, setSectionName] = useState('');
  const [sectionType, setSectionType] = useState<'standard' | 'custom' | 'appendix' | 'table' | 'figure'>('custom');
  const [contentMode, setContentMode] = useState<'ai' | 'manual'>('ai');
  const [question, setQuestion] = useState('');
  const [manualContent, setManualContent] = useState('');
  const [position, setPosition] = useState<number | undefined>(defaultPosition);

  const handleSubmit = () => {
    if (!sectionName.trim()) {
      alert('Section name is required');
      return;
    }

    if (contentMode === 'ai' && !question.trim()) {
      alert('Question is required for AI-generated sections');
      return;
    }

    if (contentMode === 'manual' && !manualContent.trim()) {
      alert('Content is required for manual sections');
      return;
    }

    // Create section object (will be sent to API)
    const section: Partial<ReportSection> = {
      id: '', // Will be set by API
      section_name: sectionName,
      section_type: sectionType,
      content_mode: contentMode,
      question: contentMode === 'ai' ? question : sectionName,
      answer: contentMode === 'manual' ? manualContent : undefined,
      display_order: position ?? 0,
    };

    onSectionCreated(section as ReportSection);
    
    // Reset form
    setSectionName('');
    setSectionType('custom');
    setContentMode('ai');
    setQuestion('');
    setManualContent('');
    setPosition(undefined);
  };

  const handleCancel = () => {
    setSectionName('');
    setSectionType('custom');
    setContentMode('ai');
    setQuestion('');
    setManualContent('');
    setPosition(undefined);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Add Custom Section"
      size="lg"
    >
      <div className="space-y-4">
        {/* Section Name */}
        <Input
          label="Section Name"
          value={sectionName}
          onChange={(e) => setSectionName(e.target.value)}
          placeholder="e.g., Executive Summary, Appendix A"
          required
        />

        {/* Section Type */}
        <Select
          label="Section Type"
          value={sectionType}
          onChange={(e) => setSectionType(e.target.value as any)}
          options={[
            { value: 'standard', label: 'Standard' },
            { value: 'custom', label: 'Custom' },
            { value: 'appendix', label: 'Appendix' },
            { value: 'table', label: 'Table' },
            { value: 'figure', label: 'Figure' },
          ]}
        />

        {/* Content Mode */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Content Mode
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="ai"
                checked={contentMode === 'ai'}
                onChange={(e) => setContentMode(e.target.value as 'ai' | 'manual')}
                className="w-4 h-4"
              />
              <Brain className="h-4 w-4" />
              <span>AI Generated (RAG)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="manual"
                checked={contentMode === 'manual'}
                onChange={(e) => setContentMode(e.target.value as 'ai' | 'manual')}
                className="w-4 h-4"
              />
              <Type className="h-4 w-4" />
              <span>Manual Entry</span>
            </label>
          </div>
        </div>

        {/* AI Mode: Question */}
        {contentMode === 'ai' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Question for AI Generation
            </label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What should this section cover? (e.g., Describe our approach to quality assurance)"
              rows={4}
              required
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
            />
          </div>
        )}

        {/* Manual Mode: Content */}
        {contentMode === 'manual' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Section Content
            </label>
            <textarea
              value={manualContent}
              onChange={(e) => setManualContent(e.target.value)}
              placeholder="Enter the section content..."
              rows={8}
              required
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
            />
          </div>
        )}

        {/* Position (Optional) */}
        <Input
          label="Position (optional)"
          type="number"
          value={position?.toString() || ''}
          onChange={(e) => setPosition(e.target.value ? parseInt(e.target.value) : undefined)}
          placeholder="Leave empty to append at end"
          min={0}
        />

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            <FileText className="h-4 w-4 mr-2" />
            Create Section
          </Button>
        </div>
      </div>
    </Modal>
  );
}
